import type { SearchParams } from '@ksyos/router';
import { useSearchParams } from '@ksyos/router';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState, ISessionUser } from '@frontend/configureStore';
import { isUuid } from '@common/utils/uuid';
import { changeToEmployment } from '@frontend/components/elements/navigation/changeToEmployment';
import { getEmploymentByPractitionerIdAndOrganizationId } from '@frontend/services/api/employmentsApiService';
import { fetchSession } from '@frontend/services/api/sessionApiService';
import { window } from '@frontend/utils/window';
import { useAbortSignal } from './useAbortSignal';

interface UseAutoSwitchOrganizationProps {
    forwardTo: (updatedSession: ISessionUser) => string;
    nextOrganizationId: string | undefined;
    showWarningModal: boolean | undefined;
}

/**
 * This custom hook automatically switches the organization employment based on the provided parameters.
 * If the `nextOrganizationId` param is omitted, it will check the URL for the "`switchToOrganizationId=...`" query parameter.
 * This is done, so we can switch the employment automatically when the user clicks a link from an e-mail while still having another employment selected.
 * @param {string} props.forwardTo - The URL to forward to after switching employment. This is a function that also provides the updated session.
 * @param {string | undefined} props.nextOrganizationId - organizationId of the employment to switch to.
 * @param {boolean | undefined} props.showWarningModal - a boolean to show a warning modal before switching employment.
 */
export function useAutoSwitchOrganization(props: UseAutoSwitchOrganizationProps) {
    const abortSignal = useAbortSignal();
    const [searchParams] = useSearchParams();
    const { organizationId: currentOrganizationId, id: practitionerId } = useSelector(
        (state: IReduxState) => state.authentication
    );

    const { organizationIdToSwitchTo, shouldSwitchToOrganization } = getOrganizationToSwitchTo(
        searchParams,
        currentOrganizationId,
        props.nextOrganizationId
    );

    useEffect(() => {
        if (!props.showWarningModal && shouldSwitchToOrganization && organizationIdToSwitchTo) {
            autoSwitchToOrganization(practitionerId, organizationIdToSwitchTo, props.forwardTo, abortSignal);
        }
    }, [shouldSwitchToOrganization, practitionerId, organizationIdToSwitchTo]);

    return {
        autoSwitchToOrganization: async () =>
            shouldSwitchToOrganization && organizationIdToSwitchTo
                ? await autoSwitchToOrganization(practitionerId, organizationIdToSwitchTo, props.forwardTo, abortSignal)
                : null,
        shouldSwitchToOrganization,
    };
}

function getOrganizationToSwitchTo(
    searchParams: SearchParams,
    selectedOrganizationId: string,
    nextOrganizationId: UseAutoSwitchOrganizationProps['nextOrganizationId']
) {
    const nextOrganizationIdFromUrl = searchParams.getOptional('switchToOrganizationId');

    const organizationIdToSwitchTo = nextOrganizationId ?? nextOrganizationIdFromUrl;

    const shouldSwitchToOrganization =
        organizationIdToSwitchTo !== undefined &&
        isUuid(organizationIdToSwitchTo) &&
        organizationIdToSwitchTo !== selectedOrganizationId;

    return { organizationIdToSwitchTo, shouldSwitchToOrganization };
}

async function autoSwitchToOrganization(
    practitionerId: string,
    organizationId: string,
    forwardTo: UseAutoSwitchOrganizationProps['forwardTo'],
    signal: AbortSignal
) {
    const nextEmployment = await getEmploymentByPractitionerIdAndOrganizationId(practitionerId, organizationId, signal);

    if (!nextEmployment) {
        throw new Error(
            `No employment found for practitionerId: ${practitionerId} and organizationId: ${organizationId}`
        );
    }

    if (nextEmployment) {
        await changeToEmployment(nextEmployment.id, signal, async () => {
            const updatedSession = await fetchSession(signal);

            // FIXME CS-22747 - the redirect doesn't seem to work with the useNavigate.
            // The above story will export the `redirect` function from `react-router` and use it here.
            window.redirect(forwardTo(updatedSession));
        });
    }
}
